/**
 * Collapse the staged overdue actuals into a single `actuals_list` payload for
 * one batched workbench apply — replacing the per-row /start|/complete serial
 * loop that froze the UI (one request + one refetch per staged row). The whole
 * batch commits in one request/transaction under one batch_id.
 *
 * Mirrors the rules the old serial commit enforced:
 *   - a start only counts when it differs from the already-recorded actual_start
 *   - a finish only counts when it differs from the recorded actual_finish, AND
 *     a finish on a not-yet-started row injects an actual_start (its planned
 *     start, else the finish) so the backend doesn't reject finish-without-start
 */
import type { WorkbenchActuals } from "../../../models";
import type { StatusActivity } from "../../../models";
import type { Staged } from "./staged";
import { iso10 } from "./dates";

export function buildActualsList(
  staged: Record<number, Staged>,
  byId: Map<number, StatusActivity>,
): WorkbenchActuals[] {
  const out: WorkbenchActuals[] = [];
  for (const [idStr, s] of Object.entries(staged)) {
    const id = Number(idStr);
    const act = byId.get(id);
    if (!act) continue;

    const item: WorkbenchActuals = { activity_id: id };
    let has = false;

    if (s.start && s.start !== iso10(act.actual_start)) {
      item.actual_start = s.start;
      has = true;
    }
    if (s.finish && s.finish !== iso10(act.actual_finish)) {
      // A finish on a not-started row (no staged/recorded start) needs a start
      // first — inject the planned start, falling back to the finish itself.
      if (!act.actual_start && !s.start) {
        item.actual_start = iso10(act.start) || s.finish;
      }
      item.actual_finish = s.finish;
      has = true;
    }

    // A stale planned start can land AFTER an early actual finish; the backend
    // rejects actual_start > actual_finish and 400s the WHOLE batch, so clamp
    // the start to the finish rather than let one row block every staged update.
    if (item.actual_start && item.actual_finish && item.actual_start > item.actual_finish) {
      item.actual_start = item.actual_finish;
    }

    if (has) out.push(item);
  }
  return out;
}
